/**
 * Auth helpers — thin typed wrappers over the Supabase auth SDK.
 *
 * Each function returns a consistent { error: string | null } shape for
 * easy display in forms. On success the SDK's onAuthStateChange listener
 * (set up in AuthProvider) will fire and update the auth store.
 */

import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { getPublicAppUrl } from '@/lib/native/platform';
import { disableBiometricUnlock } from '@/lib/native/biometric';

const NOT_CONFIGURED_ERR =
  'Authentication is not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env.local.';

export interface AuthResult {
  error: string | null;
  /**
   * Machine-readable reason, set for cases the UI needs to handle specially.
   * 'email_exists' → the email is already registered (signUp only).
   */
  code?: 'email_exists';
}

/**
 * Create a new account. Supabase will send a verification email
 * (assuming email-confirmation is enabled in the dashboard, which is
 * the default).
 *
 * If `inviteToken` is provided, it's appended to the email-verification
 * redirect URL so that after the user clicks the link, the verify-email
 * page can forward them to /accept-invite to complete the team join.
 */
export async function signUp(
  email: string,
  password: string,
  inviteToken?: string,
): Promise<AuthResult> {
  if (!isSupabaseConfigured || !supabase) return { error: NOT_CONFIGURED_ERR };

  const redirectBase = `${getPublicAppUrl()}/verify-email`;
  const redirectUrl = inviteToken
    ? `${redirectBase}?invite_token=${encodeURIComponent(inviteToken)}`
    : redirectBase;

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: redirectUrl,
    },
  });
  if (error) return { error: error.message };

  // Supabase does NOT return an error when the email is already registered —
  // it deliberately hides that to prevent email-address enumeration, and it
  // does not send a fresh confirmation email. The only signal is a returned
  // user whose `identities` array is empty. Detect that and tell the user the
  // address is already in use (otherwise the UI shows "check your email" for a
  // mail that will never arrive). This is an intentional, product-chosen
  // trade-off: a clearer signup UX in exchange for revealing that an email is
  // registered.
  if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
    return {
      error: 'An account with this email already exists.',
      code: 'email_exists',
    };
  }

  return { error: null };
}

/** Log in with email and password. */
export async function signIn(email: string, password: string): Promise<AuthResult> {
  if (!isSupabaseConfigured || !supabase) return { error: NOT_CONFIGURED_ERR };

  const { error } = await supabase.auth.signInWithPassword({ email, password });
  return { error: error?.message ?? null };
}

/** Log out the current user. */
export async function signOut(): Promise<AuthResult> {
  if (!isSupabaseConfigured || !supabase) return { error: NOT_CONFIGURED_ERR };

  // Forget any stored biometric session so a signed-out device can't be
  // unlocked with Face ID / Touch ID (no-op on web).
  await disableBiometricUnlock();

  const { error } = await supabase.auth.signOut();
  return { error: error?.message ?? null };
}

/** Trigger the password-reset email flow. */
export async function requestPasswordReset(email: string): Promise<AuthResult> {
  if (!isSupabaseConfigured || !supabase) return { error: NOT_CONFIGURED_ERR };

  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${getPublicAppUrl()}/reset-password`,
  });
  return { error: error?.message ?? null };
}

/** Set a new password (after a password reset link). */
export async function updatePassword(newPassword: string): Promise<AuthResult> {
  if (!isSupabaseConfigured || !supabase) return { error: NOT_CONFIGURED_ERR };

  const { error } = await supabase.auth.updateUser({ password: newPassword });
  return { error: error?.message ?? null };
}

/**
 * Resend the confirmation email for an unverified account.
 * Useful when the original verification email was lost or expired.
 */
export async function resendVerificationEmail(email: string): Promise<AuthResult> {
  if (!isSupabaseConfigured || !supabase) return { error: NOT_CONFIGURED_ERR };

  const { error } = await supabase.auth.resend({
    type: 'signup',
    email,
    options: {
      emailRedirectTo: `${getPublicAppUrl()}/verify-email`,
    },
  });
  return { error: error?.message ?? null };
}
